import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, Leaf } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";

const DEFAULT_HERO = [
  "https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1717616870208-a805ea578555?crop=entropy&cs=srgb&fm=jpg&w=800&q=85",
  "https://images.unsplash.com/photo-1624824148973-4256af8e9b29?crop=entropy&cs=srgb&fm=jpg&w=800&q=85",
];
const DEFAULT_CATEGORY_IMAGES = {
  kurti: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&w=1200&q=80",
  "co-ord": "https://images.unsplash.com/photo-1726585742251-72ac57fb9ef4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
};

export default function Home() {
  const { API } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [catImgs, setCatImgs] = useState(DEFAULT_CATEGORY_IMAGES);
  useEffect(() => {
    axios.get(`${API}/products`, { params: { featured: true } }).then(r => setFeatured(r.data));
    axios.get(`${API}/site/settings`).then(r => {
      if (r.data.hero_images?.length) setHero(r.data.hero_images);
      if (r.data.category_images) setCatImgs({ ...DEFAULT_CATEGORY_IMAGES, ...r.data.category_images });
    }).catch(() => {});
  }, [API]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="label-eyebrow">Spring · Heritage Edit</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-6">
              Threads of <em className="not-italic text-[#6B1A24]">heritage,</em><br/>
              tailored for today.
            </h1>
            <p className="mt-8 text-base text-[#5C5454] max-w-md leading-relaxed">
              Hand-embroidered kurtis and contemporary co-ord sets, crafted by artisans across India. Slow fashion with a soulful past.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-3 bg-[#6B1A24] text-[#FAFAF7] px-8 py-4 hover:bg-[#8A2532] transition-colors duration-500 uppercase text-xs tracking-[0.25em]"
              >
                Explore Collection <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/shop/co-ord"
                data-testid="hero-coord-btn"
                className="inline-flex items-center gap-3 border border-[#2D2626] text-[#2D2626] px-8 py-4 hover:bg-[#2D2626] hover:text-[#FAFAF7] transition-colors duration-500 uppercase text-xs tracking-[0.25em]"
              >
                Co-ord Edit
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="grid grid-cols-5 gap-4">
              <div className="col-span-3 img-zoom aspect-[3/4]">
                <img src={imgSrc(hero[0])} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-2 flex flex-col gap-4">
                <div className="img-zoom aspect-square">
                  <img src={imgSrc(hero[1])} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="img-zoom aspect-square">
                  <img src={imgSrc(hero[2])} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 left-6 hidden md:flex items-center gap-3 bg-[#FAFAF7] border border-[#E5DFD3] px-5 py-3">
              <Sparkles size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-xs tracking-widest uppercase">Hand-Finished · Limited Editions</span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#E5DFD3] py-5 overflow-hidden">
        <div className="marquee gap-12 font-display text-2xl text-[#6B1A24]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12 pr-12 shrink-0">
              <span>Chikankari</span><span>·</span>
              <span>Banarasi</span><span>·</span>
              <span>Bandhani</span><span>·</span>
              <span>Kantha</span><span>·</span>
              <span>Zardozi</span><span>·</span>
              <span>Block Print</span><span>·</span>
              <span>Mirror Work</span><span>·</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-eyebrow">Collections</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">Shop by craft</h2>
          </div>
          <Link to="/shop" className="hidden md:block text-xs uppercase tracking-[0.25em] text-[#6B1A24]" data-testid="categories-view-all">View All →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { name: "Kurtis", img: catImgs.kurti, to: "/shop/kurti", tag: "Ethnic Edit" },
            { name: "Co-ord Sets", img: catImgs["co-ord"], to: "/shop/co-ord", tag: "Fusion Edit" },
          ].map(c => (
            <Link key={c.name} to={c.to} data-testid={`cat-card-${c.name.toLowerCase()}`} className="img-zoom relative aspect-[4/3] group block">
              <img src={imgSrc(c.img)} alt={c.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-8 left-8 text-[#FAFAF7]">
                <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{c.tag}</div>
                <div className="font-display text-4xl mt-2">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-eyebrow">Featured</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">This week's edit</h2>
          </div>
          <Link to="/shop" className="text-xs uppercase tracking-[0.25em] text-[#6B1A24]" data-testid="featured-view-all">Shop All →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F3EFEA] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Leaf, t: "Artisan-made", d: "Each piece is hand-finished by master craftspeople from across India." },
            { icon: Truck, t: "Worldwide shipping", d: "Complimentary on orders over $150. Carbon-neutral parcels." },
            { icon: Sparkles, t: "Heirloom quality", d: "Investment pieces designed to be loved for generations." },
          ].map((v, i) => (
            <div key={i} className="flex flex-col gap-4">
              <v.icon size={28} strokeWidth={1.2} className="text-[#6B1A24]" />
              <div className="font-display text-2xl">{v.t}</div>
              <p className="text-sm text-[#5C5454] leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
